import { useEffect, useState } from "react";
import { keyframes } from "@emotion/react";
import { Box } from "@mui/joy";

import { useReducedMotion } from "../hooks/useReducedMotion";
import { useDelayedVisible } from "./documents/design/useDelayedVisible";
import { MARK_PATHS, MARK_VIEWBOX } from "./logoPaths";

/** "Draw the P" (hp, Oct 19; canvas T · Loading P, B): each piece is traced as a line, then
 *  fills, holds, and fades, in one loop. index.html draws the same loop before the app. */
const LOOP_SECONDS = 1.8;
/** Each piece's start in the loop: the bottom stroke, then the top bar, then the bowl
 *  (`MARK_PATHS` order is top bar, bottom stroke, bowl). */
const PIECE_DELAYS = [0.12, 0, 0.24];
/** A moment in the loop when every piece is whole (filled, not yet fading), so stopping there
 *  looks like no stop: the last piece has filled (55% of the loop, plus its delay) and the first
 *  has not begun to fade (85%, plus its delay); the middle of that window, 1.38 s. */
export const WHOLE_AT_SECONDS =
    (0.55 * LOOP_SECONDS +
        Math.max(...PIECE_DELAYS) +
        0.85 * LOOP_SECONDS +
        Math.min(...PIECE_DELAYS)) /
    2;
/** The traced line's width on screen, whatever the mark's size. */
const TRACE_PX = 1.5;

// `pathLength="1"` on each piece makes 1 the whole outline, so the dash runs 0 → 1 at any size.
const draw = keyframes`
    0% { stroke-dashoffset: 1; fill-opacity: 0; opacity: 1; }
    40% { stroke-dashoffset: 0; fill-opacity: 0; }
    55% { fill-opacity: 1; }
    85% { stroke-dashoffset: 0; fill-opacity: 1; opacity: 1; }
    100% { stroke-dashoffset: 0; fill-opacity: 1; opacity: 0; }
`;

function loadingSx(size: number) {
    return {
        "& path": {
            stroke: "currentColor",
            strokeWidth: (TRACE_PX * 260) / size,
            strokeLinejoin: "round",
            strokeDasharray: 1,
            animation: `${draw} ${LOOP_SECONDS}s ease-in-out infinite both`,
        },
        ...Object.fromEntries(
            PIECE_DELAYS.map((delay, i) => [
                `& path:nth-of-type(${i + 1})`,
                { animationDelay: `${delay}s` },
            ]),
        ),
    };
}

/**
 * Primepoint's mark, in the primary blue in either theme. `loading` makes it the app's loading
 * indicator, for a wait with nothing else to show (signing in, opening a project), and in the app
 * bar while anything on screen waits (`useMarkLoading`); a value inside a page still shows the plain
 * "…". `label` names it for screen readers; without one it is decor.
 */
export default function PrimepointMark({
    size = 27,
    loading = false,
    label,
}: {
    size?: number;
    loading?: boolean;
    label?: string;
}) {
    // People who turn motion off see the mark still and whole. The hook runs on every render.
    const reducedMotion = useReducedMotion();
    const animated = loading && !reducedMotion;
    return (
        <Box
            component="svg"
            width={size}
            height={Math.round((size * 232) / 260)}
            viewBox={MARK_VIEWBOX}
            role={label ? "img" : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            aria-busy={loading || undefined}
            data-loading={loading || undefined}
            sx={{
                display: "block",
                color: "primary.500",
                flexShrink: 0,
                ...(animated ? loadingSx(size) : {}),
            }}
        >
            {MARK_PATHS.map((d) => (
                <path key={d} d={d} fill="currentColor" pathLength={1} />
            ))}
        </Box>
    );
}

/**
 * `loading` for a mark that stands for a wait which comes and goes (the app bar's, while anything
 * on screen waits): it starts only once `waiting` has lasted `SKELETON_DELAY`, so a fast answer
 * never starts it, and when the wait ends it runs on to the next moment the mark is whole, so it
 * never stops mid-draw. A wait that comes back before then keeps it running.
 */
export function useMarkLoading(waiting: boolean): boolean {
    const due = useDelayedVisible(waiting);
    const reducedMotion = useReducedMotion();
    // When the loop began, while it runs.
    const [since, setSince] = useState<number | null>(null);
    useEffect(() => {
        if (since === null) {
            if (due) setSince(performance.now());
            return undefined;
        }
        if (waiting) return undefined;
        const into = ((performance.now() - since) / 1000) % LOOP_SECONDS;
        const left = reducedMotion ? 0 : (WHOLE_AT_SECONDS - into + LOOP_SECONDS) % LOOP_SECONDS;
        const timer = window.setTimeout(() => setSince(null), left * 1000);
        return () => window.clearTimeout(timer);
    }, [due, waiting, since, reducedMotion]);
    return since !== null;
}
